import type { CategoryFiltersState } from '~/types/database'

/**
 * Composable for managing category filter state
 * Syncs filter state with URL query parameters for shareable/bookmarkable URLs
 */
export const useCategoryFilters = () => {
  const route = useRoute()
  const router = useRouter()

  /**
   * Parse filters from URL query parameters
   */
  const parseFiltersFromUrl = (): CategoryFiltersState => {
    const query = route.query
    const filters: CategoryFiltersState = {}

    for (const [key, value] of Object.entries(query)) {
      // Skip non-filter params
      if (key === 'page' || key === 'q') continue

      if (value === null || value === undefined) continue

      // Handle array values (multi-select)
      if (Array.isArray(value)) {
        filters[key] = value.filter(v => v !== null) as string[]
      } else if (typeof value === 'string') {
        // Try to parse as boolean
        if (value === 'true') {
          filters[key] = true
        } else if (value === 'false') {
          filters[key] = false
        } else if (value === 'all' || value === '') {
          // Skip 'all' values - they mean no filter
          continue
        } else {
          filters[key] = value
        }
      }
    }

    return filters
  }

  // Reactive filter state initialized from URL
  const filters = reactive<CategoryFiltersState>(parseFiltersFromUrl())

  /**
   * Update URL query parameters based on current filters
   */
  const updateUrl = () => {
    const query: Record<string, string | string[] | undefined> = {}

    // Preserve search query if exists
    if (route.query.q) {
      query.q = route.query.q as string
    }

    // Add filter params
    for (const [key, value] of Object.entries(filters)) {
      if (value === null || value === undefined || value === '' || value === 'all') {
        continue
      }

      if (typeof value === 'boolean') {
        query[key] = value.toString()
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          query[key] = value as string[]
        }
      } else {
        query[key] = value as string
      }
    }

    // Update URL without navigation
    router.replace({ query })
  }

  /**
   * Set a single filter value
   */
  const setFilter = <K extends keyof CategoryFiltersState>(
    key: K,
    value: CategoryFiltersState[K]
  ) => {
    if (value === null || value === undefined || value === '' || value === 'all') {
      delete filters[key]
    } else {
      filters[key] = value
    }
    updateUrl()
  }

  /**
   * Set multiple filters at once
   */
  const setFilters = (newFilters: CategoryFiltersState) => {
    // Clear existing filters
    for (const key of Object.keys(filters)) {
      delete filters[key]
    }
    // Set new filters
    for (const [key, value] of Object.entries(newFilters)) {
      if (value !== null && value !== undefined && value !== '' && value !== 'all') {
        filters[key] = value
      }
    }
    updateUrl()
  }

  /**
   * Clear all filters
   */
  const clearFilters = () => {
    for (const key of Object.keys(filters)) {
      delete filters[key]
    }
    // Clear URL params except search
    const query: Record<string, string | undefined> = {}
    if (route.query.q) {
      query.q = route.query.q as string
    }
    router.replace({ query })
  }

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = computed(() => {
    return Object.keys(filters).length > 0
  })

  /**
   * Get count of active filters
   */
  const activeFilterCount = computed(() => {
    return Object.keys(filters).length
  })

  /**
   * Get array of active filter keys
   */
  const activeFilterKeys = computed(() => {
    return Object.keys(filters)
  })

  // Watch for route changes (back/forward navigation)
  watch(() => route.query, () => {
    const parsed = parseFiltersFromUrl()

    // Clear existing and set new
    for (const key of Object.keys(filters)) {
      if (!(key in parsed)) {
        delete filters[key]
      }
    }
    for (const [key, value] of Object.entries(parsed)) {
      filters[key] = value
    }
  })

  return {
    filters: readonly(filters),
    setFilter,
    setFilters,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
    activeFilterKeys
  }
}
